import { Component, ElementRef, HostBinding, HostListener, inject, ViewChild } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { MenuItem } from 'primeng/api';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { ObjectActionWithObject } from '../../models/object-action-with-object.model';
import { ObjectAction } from '../../models/object-action.model';
import { TableCellButton } from '../../models/table-cell-button.model';
import { TableCellEvent } from '../../models/table-cell-event.model';
import { TableCell } from '../../models/table-cell.model';
import { TableRow } from '../../models/table-row.model';
import { TableCellType } from '../../netgen/TableCellType';
import { TableColumn } from '../../netgen/TableColumn';
import { SmTableComponent } from '../sm-table/sm-table.component';
import { ToolTipDefinition } from '../../models/ToolTipDefinition.model';

@Component({
    selector: 'sm-table-cell',
    templateUrl: './sm-table-cell.component.html',
    styleUrl: './sm-table-cell.component.scss',
    standalone: false
})
  export class SmTableCellComponent implements ICellRendererAngularComp {

    static globalActions: ObjectAction[] = [];

    DEFAULT_TIPPY_PROPS = {
        placement: 'auto',
        strategy: 'fixed',
        popperOptions: {
            modifiers: [
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: 'viewport',
                        padding: 8
                    }
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['top', 'bottom', 'right', 'left']
                    }
                }
            ]
        }
    };

    _Utils = Utils;
    _TableCellType = TableCellType;

    app = inject(MainAppService);

    column: TableColumn;
    parentTable: SmTableComponent;
    params: ICellRendererParams;
    tableRow: TableRow;
    displayValue: string;
    mouseInside = false;
    tableCell: TableCell;
    toolTip: ToolTipDefinition;
    buttonActions: ObjectActionWithObject[] = [];

    @ViewChild("value", {read: ElementRef})
    value: ElementRef<HTMLElement>;

    //Damit Buttons vertikal zentriert angezeigt werden
    @HostBinding('class.height100p')
    get height100pClass(): boolean {
        return this.column?.cellType == TableCellType.button;
    }

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.column = this.params.colDef.context.tableColumn;
        this.parentTable = this.params.colDef.context.table;
        this.tableRow = this.params.data as TableRow;
        this.tableCell = Objects.castOrNull(this.tableRow.values[this.column.id], TableCell);
        this.buttonActions = this.getObjectActionsFromProviders();
        this.displayValue = this.params.valueFormatted;
        this.toolTip = this.tableCell?.toolTip ?? this.tableRow.toolTip;

        if (this.column.cellRenderer != null) {
            this.displayValue = this.column.cellRenderer(params);
        }
        let wrapper = Arrays.getSafe(this.params.eGridCell?.children, 0);
        if (wrapper != null) {
            //Das benötigen wir damit, z.B. Buttons vertikal zentriert angezeigt werden
            wrapper.classList?.add("height100p");
        }
    }
        
    refresh(params: ICellRendererParams): boolean {
        this.agInit(params);
        return true;
    }

    getKeepHtml(): boolean {
        return this.column?.cellRenderer != null || this.column?.keepHtml || this.tableCell?.keepHtml;
    }

    async buttonClick(event: any, button: TableCellButton): Promise<void> {
        if (this.column.id == SmTableComponent.COLUMN_ID_REMOVE) {
            event.preventDefault();
            event.stopPropagation();
            await this.parentTable.removeRow(this.tableRow, true);
        }
        else if (this.column.id == SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN) {
            await this.parentTable.editRowFullScreen(this.params.node);
        }
        else {
            if (this.column.cellType == TableCellType.link) {
                event.preventDefault();
                event.stopPropagation();
            }
            this.parentTable.onCellClicked.emit(new TableCellEvent(button, this.tableRow, this.params.colDef, 0, 0));
        }
    }

    getObjectActionsFromProviders(): ObjectActionWithObject[] {
        return Arrays.explode(this.tableCell?.actionProviders ?? [], ap => Arrays.explode(this.tableCell.internal, key => ap.getActions(key).map(a => new ObjectActionWithObject(a, key)))) ?? [];
    }

    getObjectActions(): ObjectActionWithObject[] {
        let s = Objects.toString(this.params.value);
        return [
            new ObjectActionWithObject({
                getCaption: () => "In Zwischenablage kopieren",
                getIcon: () => "fas fa-clipboard",
                isUsable: value => Strings.is(value),
                perform: async value => {
                    await GuiUtils.copyToClipboard(value as string);
                    return null;
                }
            }, s),
            new ObjectActionWithObject({
                getCaption: () => "Text in Fenster öffnen",
                getIcon: () => "fas fa-window-maximize",
                isUsable: value => Strings.is(value),
                perform: value => {
                    this.app.dialog.textArea("Zellentext", value as string, null, true);
                    return null;
                }
            }, s),
            new ObjectActionWithObject({
                getCaption: () => "Texte dieser Spalte in Fenster öffnen",
                getIcon: () => "fas fa-window-maximize",
                isUsable: _ => true,
                perform: _ => {
                    this.app.dialog.textArea("Spaltenwerte", Arrays.itemsToString(this.parentTable.getUnfilteredRows(), "\n", row => TableCell.getValue(row.values[this.params.colDef.colId])), null, true);
                    return null;
                }
            }, s),
            new ObjectActionWithObject({
                getCaption: () => "Nur Zeilen mit diesem Wert anzeigen",
                getIcon: () => "fas fa-filter",
                isUsable: _ => this.parentTable.allowFilter,
                perform: (value) => {
                    this.parentTable.setFilter(this.params.colDef.colId, value);
                    return null;
                }
            }, s),
            ...SmTableCellComponent.globalActions.map(a => new ObjectActionWithObject(a, s)),
            ...this.tableCell?.internal == null || this.tableCell?.actionProviders == null ? [] :
                Arrays.explode(this.tableCell.actionProviders, ap => Arrays.explode(this.tableCell.internal, key => ap.getActions(key).map(a => new ObjectActionWithObject(a, key))))
        ];
    }

    actionButtonClick(event: any, action: ObjectActionWithObject): void {
        action.perform();
        event.preventDefault();
        event.stopPropagation();
    }

    actionContextMenuButtonClick(event: any): void {
        let menu: MenuItem[] = this.buttonActions.map(a => ({
            label: a.action.getCaption(a.on),
            icon: a.getIcon(),
            command: _ => a.perform()
        }));
        this.app.openContextMenu(menu);
        event.preventDefault();
        event.stopPropagation();
    }


    actionContextButtonClick(event: any): void {
        let menu: MenuItem[] = this.getObjectActions().filter(a => a.action.isUsable(a.on)).map(a => ({
            label: a.action.getCaption(a.on),
            icon: "fas fa-open",
            command: _ => a.perform()
        }));
        this.app.openContextMenu(menu);
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('mouseenter')
    mouseEnter(): void {
        this.mouseInside = true;
    }

    @HostListener('mouseleave')
    mouseLeave(): void {
        this.mouseInside = false;
    }

    addRow(): void {
        this.parentTable.addRow();
    }

    handleTooltip(event: any): void {
        const element = event.element;
        element.setAttribute('title', Objects.toString(this.displayValue));
    }

  }